import type { HostAdapter } from "../host/adapter";

/**
 * Where a frame says it is: the workspace, the project, and the URL a human
 * would open to get back to it.
 *
 * ## Why this is shared, and why it is not a guess
 *
 * **Every frame in one session must derive its workspace id the same way**, or
 * two frames in the same Giraffe workspace register under different ids, land
 * in different `SessionRegistry` buckets, and never appear in one session. That
 * is the failure mode Control 3 would report as "the second frame never
 * joined", pointing at the socket rather than at this function.
 *
 * It comes from `getUrlParams`, which is the host's OWN routing context —
 * `routeManager.params.selectedOrgId` and `.activeProjectId`, read at
 * `libs/gi-js/src/lib/exports/misc.ts:124` (giraffe-four @ `50cad4903`,
 * 2026-10-12). Earlier code here read `giraffeState.get("project")` and reached
 * for a `workspace_id` property, which was **inferred and never verified**; the
 * state key's shape is not documented anywhere this workspace has checked.
 *
 * ⚠️ **`activeWorkspace` and `activeProject` are returned unconditionally.**
 * Only `queryParams` is narrowed for a non-privileged caller (`isPrivilegedUrl`
 * in the same file), so an app served from a non-`giraffe.build` origin gets
 * the identical answer. That is what lets a registered app on our Cloudflare
 * account sit in the same session as a localhost one.
 *
 * ⚠️ Provenance: a HOST-SOURCE READING — verified against the current build,
 * stronger than inferred, weaker than documented, and **not a contract**.
 */

export type ProjectIdentity = {
  readonly workspaceId: string;
  readonly projectId: string;
  /** Where a human would reopen this. Empty when the host gave nothing to build one from. */
  readonly projectUrl: string;
};

export type ProjectIdentityResult =
  | { readonly ok: true; readonly identity: ProjectIdentity }
  | { readonly ok: false; readonly reason: string };

function nonEmpty(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * Ask the host where this frame is.
 *
 * Returns a REASON rather than defaulting, because every default here is
 * wrong in a way nothing downstream can detect: a frame that invents a
 * workspace id registers successfully, appears in the panel, and silently
 * fails to join the session its sibling is in.
 */
/**
 * One sentence for both ways the host can fail to say where this frame is —
 * refusing the call, and answering something that is not an object.
 *
 * They are one sentence rather than two because the reader can do the same
 * thing about either, which is the whole test for what crosses to a panel: name
 * an action, or say only that there is none. The two used to differ, and the
 * difference they carried — the RPC's name, the thrown text, "the host moved" —
 * was information for us, shown to somebody who cannot use it. That detail is
 * still collected; it goes to the console now.
 */
const HOST_DID_NOT_SAY =
  "Giraffe did not tell this panel which project it is in. Close the app panel and reopen it " +
  "from inside a saved Giraffe project.";

export async function readProjectIdentity(host: HostAdapter): Promise<ProjectIdentityResult> {
  let answer: unknown;
  try {
    answer = await host.invoke("getUrlParams", []);
  } catch (cause) {
    // ⚠️ The thrown value is CONSOLE detail. It is the host's text, not ours,
    // so nothing we write here bounds what it says — and `reason` is rendered
    // straight into the app panel by every caller of this function.
    console.log({
      event: "identity.get_url_params_threw",
      message: cause instanceof Error ? cause.message : String(cause),
    });
    return { ok: false, reason: HOST_DID_NOT_SAY };
  }
  if (typeof answer !== "object" || answer === null) {
    console.log({ event: "identity.get_url_params_shape", answered: typeof answer });
    return { ok: false, reason: HOST_DID_NOT_SAY };
  }
  const params = answer as Record<string, unknown>;
  const workspaceId = nonEmpty(params.activeWorkspace);
  const projectId = nonEmpty(params.activeProject);
  if (workspaceId === null) {
    return {
      ok: false,
      reason:
        "The host named no active workspace. An app panel outside a project cannot join a " +
        "session — open a SAVED project, not /work/space/scratchpad.",
    };
  }
  if (projectId === null) {
    return {
      ok: false,
      reason:
        "The host named no active project. A /work/space/scratchpad URL looks identical to a " +
        "project URL, has no project row, and cannot persist an app placement.",
    };
  }

  const hostName = nonEmpty(params.host);
  const pathname = nonEmpty(params.pathname);
  return {
    ok: true,
    identity: {
      workspaceId,
      projectId,
      // Built from what the host reported rather than from `window.location`,
      // which inside an app frame is the APP's URL and not the project's.
      projectUrl: hostName === null || pathname === null ? "" : `https://${hostName}${pathname}`,
    },
  };
}
